/**
 * Performance Loader v2.0
 * Defers non-critical service loading to improve initial page load.
 * Adds smart preloading, connection-aware loading, and periodic memory cleanup.
 */

import { detectMemoryPressure, cleanupLocalStorage, isSlowConnection } from './performanceHelpers';

let servicesLoaded = false;
let componentsPreloaded = false;
let memoryCleanupInterval: ReturnType<typeof setInterval> | null = null;

// Services to load after initial render (non-critical)
const deferredServices = async () => {
    if (servicesLoaded) return;
    
    console.log('[Perf] Loading deferred services...');
    const startTime = performance.now();
    
    try {
        // Load these services in parallel after initial render
        await Promise.all([
            import('../services/aiLearningService'),
            import('../services/popupBannerService'),
            import('../services/ipRateLimiter'),
        ]);
        
        servicesLoaded = true;
        const loadTime = performance.now() - startTime;
        console.log(`[Perf] Deferred services loaded in ${loadTime.toFixed(2)}ms`);
    } catch (error) {
        console.error('[Perf] Error loading deferred services:', error);
    }
};

/**
 * Preload likely-needed lazy components after first user interaction.
 * Only runs on fast connections to avoid wasting bandwidth.
 */
const preloadCriticalComponents = async () => {
    if (componentsPreloaded || isSlowConnection()) return;
    componentsPreloaded = true;

    const startTime = performance.now();
    try {
        // These are the most commonly opened modals/components
        await Promise.all([
            import('../components/AuthModal'),
            import('../components/UpgradeModal'),
            import('../components/profile/ProfileDropdown'),
        ]);
        console.log(`[Perf] Critical components preloaded in ${(performance.now() - startTime).toFixed(1)}ms`);
    } catch {
        // Silent fail for preloads
    }
};

/**
 * Run periodic memory cleanup to prevent localStorage bloat.
 * Cleans up stale ad impressions, old session data, and expired caches.
 */
const startMemoryCleanup = () => {
    if (memoryCleanupInterval) return;

    const cleanup = () => {
        const { isUnderPressure, usagePercent } = detectMemoryPressure();
        if (isUnderPressure) {
            console.warn(`[Perf] Memory pressure detected (${(usagePercent * 100).toFixed(0)}%). Running cleanup...`);
            const removed = cleanupLocalStorage(
                ['luca_ad_', 'luca_cache_', 'luca_temp_', 'ledger_session_backup_'],
                3 * 24 * 60 * 60 * 1000 // 3 days for aggressive cleanup
            );
            if (removed > 0) console.log(`[Perf] Cleaned up ${removed} stale localStorage entries`);
        }
    };

    // Run once immediately, then every 5 minutes
    cleanup();
    memoryCleanupInterval = setInterval(cleanup, 5 * 60 * 1000);
};

/**
 * Initialize deferred loading after page becomes interactive
 */
export const initDeferredLoading = () => {
    if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', () => {
            scheduleDeferredLoad();
        });
    } else {
        scheduleDeferredLoad();
    }

    // Preload components on first user interaction (click, touch, key)
    const onFirstInteraction = () => {
        preloadCriticalComponents();
        window.removeEventListener('click', onFirstInteraction);
        window.removeEventListener('touchstart', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
    };
    window.addEventListener('click', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true });

    // Start periodic memory cleanup
    startMemoryCleanup();
};

/**
 * Schedule deferred services to load during idle time
 */
const scheduleDeferredLoad = () => {
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
            deferredServices();
        }, { timeout: 2000 });
    } else {
        setTimeout(() => {
            deferredServices();
        }, 1000);
    }
};

/**
 * Preload critical resources (fonts, images)
 */
export const preloadCriticalResources = () => {
    // Preconnect to Firebase
    addResourceHint('preconnect', 'https://firestore.googleapis.com');
    addResourceHint('preconnect', 'https://www.googleapis.com');
    
    // DNS prefetch for external services
    addResourceHint('dns-prefetch', 'https://fonts.googleapis.com');
    addResourceHint('dns-prefetch', 'https://ipapi.co');

    // Preload the mascot image (used in sidebar + header)
    addResourceHint('preload', '/robot-luca.png', 'image');
};

/**
 * Add a resource hint link element to the document head.
 */
function addResourceHint(rel: string, href: string, as?: string) {
    // Avoid duplicates
    if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (as) link.setAttribute('as', as);
    if (rel === 'preconnect' || rel === 'preload') link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
}

/**
 * Mark when app is fully interactive
 */
export const markInteractive = () => {
    if (performance && performance.mark) {
        performance.mark('app-interactive');
        
        if (performance.getEntriesByType) {
            const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            if (navEntry) {
                const interactiveTiming = performance.now();
                console.log(`[Perf] Time to interactive: ${interactiveTiming.toFixed(2)}ms`);
            }
        }
    }

    // Preload resources after marking interactive
    preloadCriticalResources();
};

/**
 * Stop all background performance tasks (for cleanup on unmount).
 */
export const stopPerformanceTasks = () => {
    if (memoryCleanupInterval) {
        clearInterval(memoryCleanupInterval);
        memoryCleanupInterval = null;
    }
};
